/** 带壳工作台嵌套路由前缀（与 React Router 父 path 一致） */
export const APP_LAYOUT_BASE = "/hub";

/**
 * 官网首页宣传落地页（与 dynamicRoutes 静态路由 `/home` 一致，无需登录，原 `/marketing`）。
 * 注意区分：`/home` 是营销页；平台功能精品译制的代码在 `src/pages/home/`（组件目录叫 home），
 * 路由是 `/hub/premium-translation`，两者无关。
 */
export const MARKETING_PAGE_PATH = "/home";

/** 开放平台 API 文档页（无需登录，复用 marketing 导航与页脚） */
export const API_DOC_PAGE_PATH = "/api_doc";

/** 精品译制页完整 path（嵌套在 /hub 下） */
export const PREMIUM_TRANSLATION_FULL_PATH = `${APP_LAYOUT_BASE}/premium-translation`;

/** 一键速译外层列表（嵌套在 /hub 下，与精品译制共用工作台壳） */
export const QUICK_TRANSLATE_FULL_PATH = `${APP_LAYOUT_BASE}/quick-translate`;

/** 精品译制 · 内层剧集页（嵌套在 /hub 下，query: seriesId、seriesName） */
export const PREMIUM_SERIES_INNER_BASE = `${APP_LAYOUT_BASE}/premium-series`;

/** 一键速译 · 内层剧集页（嵌套在 /hub 下，query: seriesId、seriesName；无原剧 Hero） */
export const QUICK_SERIES_INNER_BASE = `${APP_LAYOUT_BASE}/quick-series-inner`;

/**
 * 外层剧集列表分页：从外层点进内层时在 query 上携带，壳层「返回」拼回外层 `page`。
 * （浏览器后退仍依赖历史栈里的完整 URL；壳层 NavLink 需显式带参。）
 */
export const OUTER_SERIES_LIST_PAGE_PARAM = "outerListPage";

/**
 * 内层剧集（语种列表）分页：从内层进入重构视频列表时在 query 携带（避免与视频列表自身的 `page` 冲突）；
 * 壳层「返回」拼回内层 URL 的 `page`。
 */
export const INNER_SERIES_LIST_PAGE_PARAM = "innerListPage";

/** 精品/速译共用 · 重构视频列表（嵌套在 /hub 下；速译带 `from=quick`） */
export const PREMIUM_VIDEO_LIST_REFACTOR_BASE = `${APP_LAYOUT_BASE}/premium-video-list-refactor`;

/** 视频编辑页返回：工作台内重构视频列表（无 langCode=原剧列表；有 langCode=该语种列表；速译带 from=quick） */
export function buildPremiumVideoListRefactorReturnAfterVideoEditUrl(options: {
  seriesId: string | number;
  seriesName?: string;
  langCode?: string | null;
  langName?: string | null;
  /** 从原剧/纯净列表进入编辑时为 true：不传 langCode，与 PremiumVideoListRefactorPage 一致 */
  isPure?: boolean;
  /** 一键速译工作台进入编辑时带 true */
  fromQuick?: boolean;
  /** 返回视频列表时恢复的客户端分页页码（>1 时写入 URL `page`） */
  listReturnPage?: number;
  /** 返回视频列表时保留内层语种列表页码（>1 时写入 `innerListPage`） */
  innerListPage?: number;
  /** 外层剧集列表页码（>1 时写入 `outerListPage`，便于再回到外层同一页） */
  outerListPage?: number;
}): string {
  const q = new URLSearchParams();
  q.set("seriesId", String(options.seriesId));
  const name = (options.seriesName ?? "").trim();
  if (name) q.set("seriesName", name);
  if (!options.isPure && options.langCode?.trim()) {
    q.set("langCode", options.langCode.trim());
  }
  if (options.fromQuick) {
    q.set("from", "quick");
    // 一键速译返回时用 targetLangName
    if (options.langName?.trim()) {
      q.set("targetLangName", options.langName.trim());
    }
  } else if (options.langName?.trim()) {
    // 精品译制返回时用 langName
    q.set("langName", options.langName.trim());
  }
  const p = Number(options.listReturnPage);
  if (Number.isFinite(p) && p > 1) {
    q.set("page", String(Math.floor(p)));
  }
  const ip = Number(options.innerListPage);
  if (Number.isFinite(ip) && ip > 1) {
    q.set(INNER_SERIES_LIST_PAGE_PARAM, String(Math.floor(ip)));
  }
  const op = Number(options.outerListPage);
  if (Number.isFinite(op) && op > 1) {
    q.set(OUTER_SERIES_LIST_PAGE_PARAM, String(Math.floor(op)));
  }
  return `${PREMIUM_VIDEO_LIST_REFACTOR_BASE}?${q.toString()}`;
}

/** 工作台内 · 视频上传配置（嵌套在 /hub 下；query 与 `uploadWorkspace` 页面约定对齐） */
export const PREMIUM_UPLOAD_VIDEO_WORKSPACE_PATH = `${APP_LAYOUT_BASE}/premium-upload-video`;

/** 工作台内 · 音色库（嵌套在 /hub 下；默认索引为剧集列表，与 doc/6.音色库 一致） */
export const PREMIUM_VOICE_LIBRARY_FULL_PATH = `${APP_LAYOUT_BASE}/voice-library`;

/** 工作台内 · 公共音色目录（嵌套在 voice-library 下） */
export const PREMIUM_VOICE_LIBRARY_PUBLIC_PATH = `${PREMIUM_VOICE_LIBRARY_FULL_PATH}/public`;

/** 历史子路径：路由重定向至 `PREMIUM_VOICE_LIBRARY_FULL_PATH` */
export const PREMIUM_VOICE_LIBRARY_SERIES_PATH = `${PREMIUM_VOICE_LIBRARY_FULL_PATH}/series`;

/** 工作台内 · AI 音色设计（独立模块，与音色库同级；与 doc/7.音色设计页 一致） */
export const PREMIUM_VOICE_DESIGN_FULL_PATH = `${APP_LAYOUT_BASE}/voice-design`;

/** 工作台内 · 剧集管理（原个人中心子路由，与精品译制共用壳层） */
export const PREMIUM_SERIES_MANAGE_FULL_PATH = `${APP_LAYOUT_BASE}/seriesManage`;

/** 工作台内 · 系统管理 / 基础信息（原个人中心子路由，子页面为嵌套路由） */
export const PREMIUM_SYSTEM_MANAGE_BASE = `${APP_LAYOUT_BASE}/system-manage`;

/** 与菜单合并去重 `PREMIUM_ROUTE_PATHS` 一致：所有可能由 normalize 得到的 system-manage 全路径 */
export const PREMIUM_SYSTEM_MANAGE_FULL_PATHS: string[] = [
  PREMIUM_SYSTEM_MANAGE_BASE,
  `${PREMIUM_SYSTEM_MANAGE_BASE}/basic`,
  `${PREMIUM_SYSTEM_MANAGE_BASE}/user`,
  `${PREMIUM_SYSTEM_MANAGE_BASE}/role`,
  `${PREMIUM_SYSTEM_MANAGE_BASE}/voice`,
  `${PREMIUM_SYSTEM_MANAGE_BASE}/menu`,
  `${PREMIUM_SYSTEM_MANAGE_BASE}/log/loginlog`,
  `${PREMIUM_SYSTEM_MANAGE_BASE}/log/operationlog`,
];

export function isSystemManageWorkspaceFullPath(p: string): boolean {
  const n = (p || "").trim();
  return n === PREMIUM_SYSTEM_MANAGE_BASE || n.startsWith(`${PREMIUM_SYSTEM_MANAGE_BASE}/`);
}

/** 后端菜单若仍配置旧 path，合并为与新前端一致，避免重复注册或链到旧 URL */
export const MENU_PATH_NORMALIZE: Record<string, string> = {
  "/home": PREMIUM_TRANSLATION_FULL_PATH,
  "/premium-translation": PREMIUM_TRANSLATION_FULL_PATH,
  "/quick-translate": QUICK_TRANSLATE_FULL_PATH,
  "/quickTrans": QUICK_TRANSLATE_FULL_PATH,
  /** 旧平铺路由：避免点到壳外再 Redirect 回工作台，造成侧栏整棵卸载闪烁 */
  "/voice-library": PREMIUM_VOICE_LIBRARY_FULL_PATH,
  "/songs": PREMIUM_VOICE_LIBRARY_FULL_PATH,
  "/voicePublic": "/voice-design",
  /** 侧栏 / 菜单「音色设计」：工作台内独立路由（旧平铺 `/voice-design` 仍可由 default 路由兼容） */
  "/voice-design": PREMIUM_VOICE_DESIGN_FULL_PATH,
  "/voice-clone": `${APP_LAYOUT_BASE}/voice-clone`,
  "/subtitle-trans": `${APP_LAYOUT_BASE}/subtitle-trans`,
  "/text-to-audio": `${APP_LAYOUT_BASE}/text-to-audio`,
  "/voice-ai": `${APP_LAYOUT_BASE}/ai-voice`,
  "/hub": `${APP_LAYOUT_BASE}/ai-voice`,
  "/ai-image": `${APP_LAYOUT_BASE}/ai-image`,
  "/podcast": `${APP_LAYOUT_BASE}/podcast`,
  "/ai-video": `${APP_LAYOUT_BASE}/ai-video`,
  /** AI 数字人：迁入工作台嵌套路由（与 AI 音频/生图/播客/视频同族） */
  "/ai-digital-human": `${APP_LAYOUT_BASE}/ai-digital-human`,
  /** AI 转录：迁入工作台嵌套路由 */
  "/transcription": `${APP_LAYOUT_BASE}/transcription`,
  /** 账户与账单：迁入工作台嵌套路由 */
  "/invoice-management": `${APP_LAYOUT_BASE}/invoice-management`,
  "/hub/invoice-management": `${APP_LAYOUT_BASE}/invoice-management`,
  /** 历史子路径：统一到独立模块 */
  [`${PREMIUM_VOICE_LIBRARY_FULL_PATH}/design`]: PREMIUM_VOICE_DESIGN_FULL_PATH,
  /** 剧集管理：迁入工作台壳，侧栏与菜单仍可能带旧 path */
  "/profile/seriesManage": PREMIUM_SERIES_MANAGE_FULL_PATH,

  /** 系统管理 / 基础信息：迁入工作台 system-manage 嵌套路由 */
  "/profile/basic": `${PREMIUM_SYSTEM_MANAGE_BASE}/basic`,
  "/profile/user": `${PREMIUM_SYSTEM_MANAGE_BASE}/user`,
  "/profile/role": `${PREMIUM_SYSTEM_MANAGE_BASE}/role`,
  "/profile/menu": `${PREMIUM_SYSTEM_MANAGE_BASE}/menu`,
  "/profile/voice": `${PREMIUM_SYSTEM_MANAGE_BASE}/voice`,
  "/profile/voiceManage": `${PREMIUM_SYSTEM_MANAGE_BASE}/voice`,
  "/profile/system/voice": `${PREMIUM_SYSTEM_MANAGE_BASE}/voice`,
  "/profile/system/voiceManage": `${PREMIUM_SYSTEM_MANAGE_BASE}/voice`,
  "/profile/log/loginlog": `${PREMIUM_SYSTEM_MANAGE_BASE}/log/loginlog`,
  "/profile/log/operationlog": `${PREMIUM_SYSTEM_MANAGE_BASE}/log/operationlog`,
  "/profile/system/log/loginlog": `${PREMIUM_SYSTEM_MANAGE_BASE}/log/loginlog`,
  "/profile/system/log/operationlog": `${PREMIUM_SYSTEM_MANAGE_BASE}/log/operationlog`,
};

export function normalizeMenuPath(path: string): string {
  return MENU_PATH_NORMALIZE[path] ?? path;
}

/** 后端 component 与 system-manage 子 path 段（含 log/loginlog） */
const SYSTEM_MANAGE_COMPONENT_SUFFIX: Record<string, string> = {
  "pages/manage/BasicInfo": "basic",
  "pages/manage/system/UserInfo": "user",
  "pages/manage/system/RoleManage": "role",
  "pages/manage/system/MenuManage": "menu",
  "pages/manage/system/voiceManage": "voice",
  "pages/manage/system/log/Loginlog": "log/loginlog",
  "pages/manage/system/log/Operationlog": "log/operationlog",
};

function stripMenuComponent(component: string): string {
  return (component || "").trim().replace(/^\/+/, "");
}

/**
 * 侧栏 / 个人中心跳转用：先 path 映射，再按 component 兜底到 system-manage（避免后端 path 与 /profile/voice 等不一致时仍进 Profile）。
 */
export function resolveMenuNavigationPath(path: string, component?: string | null): string {
  const p0 = (path || "").trim();
  const p1 = normalizeMenuPath(p0);
  if (isSystemManageWorkspaceFullPath(p1)) return p1;
  const c = stripMenuComponent(component || "");
  if (!c) return p1;
  const suffix = SYSTEM_MANAGE_COMPONENT_SUFFIX[c];
  if (suffix) return `${PREMIUM_SYSTEM_MANAGE_BASE}/${suffix}`;
  return p1;
}

/** 菜单项最终是否落在 system-manage 工作台（应从 /profile 嵌套路由中排除） */
export function menuPathMigratedToSystemWorkspace(
  rawMenuPath: string | null | undefined,
  component?: string | null
): boolean {
  return isSystemManageWorkspaceFullPath(resolveMenuNavigationPath(rawMenuPath || "", component));
}

type MenuLike = {
  path?: string;
  hidden?: boolean;
  component?: string;
  sort?: number;
  children?: MenuLike[];
  meta?: { title?: string };
  name?: string;
};

function sortMenuLike(a: MenuLike, b: MenuLike): number {
  return (a.sort ?? 0) - (b.sort ?? 0);
}

/** 个人中心子树中第一个迁入 system-manage 的菜单全路径（用于 /profile 无其它子路由时的默认跳转） */
export function getFirstMigratedSystemManagePathFromMenus(menus: MenuLike[]): string | null {
  const list = [...menus].filter((m) => !m.hidden).sort(sortMenuLike);
  for (const menu of list) {
    if (menu.children?.length) {
      const hit = getFirstMigratedSystemManagePathFromMenus(menu.children);
      if (hit) return hit;
    }
    const raw = (menu.path || "").trim();
    if (!raw || !menu.component) continue;
    const norm = resolveMenuNavigationPath(raw, menu.component);
    if (isSystemManageWorkspaceFullPath(norm)) return norm;
  }
  return null;
}
